import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';

interface KpiCard {
  label: string;
  value: string;
  muted?: string;
  sparkline?: string;
}

interface ExposureItem {
  label: string;
  value: number;
  colorClass: string;
}

interface HoldingRow {
  name: string;
  badge?: string;
  navStart: string;
  externalFlow: string;
  performance: string;
  performanceType: 'positive' | 'neutral' | 'negative';
  navEnd: string;
  allocation: string;
  sparkline?: boolean;
}
  
@Component({
  selector: 'app-portfolio-book',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './portfolio-details.components.html',
  styleUrls: ['./portfolio-details.components.scss']
})
export class PortfolioComponent {
  selectedClient = 'Brian Hodgson';
  activeRange = 'YTD';
  searchTerm = '';

  readonly ranges = ['YTD', 'MTD', 'QTD', 'MAX'];

  readonly navItems = [
    { icon: '⌘', label: 'Overview', active: true },
    { icon: '▥', label: 'Inventory list', active: false },
    { icon: '▤', label: 'Transaction list', active: false }
  ];

  readonly footerItems = [
    { icon: '◫', label: 'Management' },
    { icon: '↗', label: 'Investor Portal' },
    { icon: '↗', label: 'Go to Q' }
  ];

  readonly kpis: KpiCard[] = [
    {
      label: 'NAV',
      value: '1.90 m €'
    },
    {
      label: 'Total performance',
      value: '166.8 k €',
      sparkline: '18,54 32,50 45,52 58,46 72,44 86,49 101,37 116,42 130,34 145,39 160,28 174,31 190,20'
    },
    {
      label: 'Invested capital',
      value: '2.41 m €'
    },
    {
      label: 'Deposits & withdrawals',
      value: '260.03 k €'
    },
    {
      label: 'Free liquidity',
      value: '0.79 m €'
    }
  ];

  readonly chartLine = [
    '0,360', '12,352', '22,362', '32,341', '41,325', '51,317', '60,300', '70,302', '80,281', '91,278',
    '103,254', '116,251', '126,240', '136,246', '146,236', '156,252', '168,245', '178,238', '190,241',
    '202,244', '212,237', '224,229', '236,241', '248,218', '260,215', '272,217', '284,207', '296,209',
    '308,222', '320,228', '332,225', '344,236', '356,233', '368,228', '380,223', '392,231', '404,205',
    '416,212', '428,202', '440,226', '452,198', '464,205', '476,188', '488,178', '500,178', '512,180',
    '524,181', '536,194', '548,185', '560,175', '572,186', '584,172', '596,180', '608,170', '620,152',
    '632,150', '644,160', '656,143', '668,145', '680,154', '692,151', '704,169', '716,155', '728,181',
    '740,164', '752,253', '764,228', '776,214', '788,195', '800,198', '812,178', '824,170', '836,165',
    '848,185', '860,169', '872,151', '884,149', '896,145', '908,182', '920,159', '932,154', '944,160',
    '956,141', '968,133', '980,161', '992,166', '1004,154', '1016,148', '1028,132', '1040,111', '1052,108',
    '1064,121', '1076,128', '1088,116', '1100,166', '1112,146', '1124,103', '1136,117', '1148,109',
    '1160,124', '1172,103', '1184,111', '1196,102', '1208,96', '1220,77', '1232,36', '1244,64',
    '1256,54', '1268,60', '1280,112', '1292,88', '1304,52', '1316,48', '1328,82', '1340,74', '1352,92'
  ].join(' ');

  readonly chartArea = `0,360 ${this.chartLine} 1352,365 0,365`;

  readonly currencyExposure: ExposureItem[] = [
    { label: 'EUR', value: 62.50, colorClass: 'blue' },
    { label: 'USD', value: 33.57, colorClass: 'blue' },
    { label: 'JPY', value: 2.24, colorClass: 'muted' },
    { label: 'CHF', value: 1.69, colorClass: 'muted' }
  ];

  readonly countryExposure: ExposureItem[] = [
    { label: 'Germany', value: 56.96, colorClass: 'blue' },
    { label: 'USA', value: 33.57, colorClass: 'blue' },
    { label: 'France', value: 5.23, colorClass: 'muted' },
    { label: 'Japan', value: 2.24, colorClass: 'muted' },
    { label: 'Switzerland', value: 1.69, colorClass: 'muted' },
    { label: 'Other', value: 0.31, colorClass: 'muted' }
  ];

  readonly sectorExposure: ExposureItem[] = [
    { label: 'Financials', value: 55.65, colorClass: 'blue' },
    { label: 'Technology', value: 22.27, colorClass: 'blue' },
    { label: 'Consumer cyclicals', value: 10.77, colorClass: 'muted' },
    { label: 'Healthcare', value: 5.17, colorClass: 'muted' },
    { label: 'Government activity', value: 2.13, colorClass: 'muted' },
    { label: 'Other', value: 4.01, colorClass: 'muted' }
  ];

  readonly classification: ExposureItem[] = [
    { label: 'Stocks', value: 58.54, colorClass: 'cyan' },
    { label: 'Liquidity', value: 41.46, colorClass: 'yellow' }
  ];

  readonly currencyBreakdown: ExposureItem[] = [
    { label: 'EUR', value: 62.50, colorClass: 'pink' },
    { label: 'USD', value: 33.57, colorClass: 'cyan-dark' },
    { label: 'JPY', value: 2.24, colorClass: 'orange' },
    { label: 'CHF', value: 1.69, colorClass: 'purple' }
  ];

  readonly holdings: HoldingRow[] = [
    {
      name: 'Liquidity Account - Deutsche Bank (EUR)',
      badge: 'Liquidity',
      navStart: '654,001 €',
      externalFlow: '134,052 €',
      performance: '0 €',
      performanceType: 'neutral',
      navEnd: '788,053 €',
      allocation: '41.46 %'
    },
    {
      name: 'Alphabet Inc.',
      navStart: '22,731 €',
      externalFlow: '-16,639 €',
      performance: '98,884 €',
      performanceType: 'positive',
      navEnd: '104,976 €',
      allocation: '5.52 %',
      sparkline: true
    },
    {
      name: 'Deutsche Bank AG',
      navStart: '80,214 €',
      externalFlow: '0 €',
      performance: '12,608 €',
      performanceType: 'positive',
      navEnd: '92,822 €',
      allocation: '4.88 %',
      sparkline: true
    },
    {
      name: 'Apple Inc.',
      navStart: '73,770 €',
      externalFlow: '0 €',
      performance: '9,082 €',
      performanceType: 'positive',
      navEnd: '82,852 €',
      allocation: '4.36 %',
      sparkline: true
    }
  ];

  readonly tableSparkline = '0,28 12,24 24,26 36,20 48,22 60,18 72,16 84,14 96,10 108,12 120,8';

  get filteredHoldings(): HoldingRow[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      return this.holdings;
    }

    return this.holdings.filter((holding) => holding.name.toLowerCase().includes(term));
  }
}
